<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('qrGenerator.title') }}</h1>
      <p>{{ $t('qrGenerator.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="qr-layout">
        <div class="qr-form">
          <div class="form-group">
            <label>{{ $t('qrGenerator.contentLabel') }}</label>
            <textarea
              v-model="content"
              :placeholder="$t('qrGenerator.contentPlaceholder')"
              rows="4"
              class="input-area"
              @input="generateQR"
            ></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ $t('qrGenerator.sizeLabel') }}</label>
              <div class="size-control">
                <input type="range" v-model.number="size" min="100" max="500" step="10" @input="generateQR" />
                <span class="size-value">{{ size }} × {{ size }}</span>
              </div>
            </div>
          </div>

          <div class="form-row color-row">
            <div class="form-group">
              <label>{{ $t('qrGenerator.fgColor') }}</label>
              <div class="color-picker-wrapper">
                <input type="color" v-model="fgColor" @input="generateQR" />
                <span>{{ fgColor }}</span>
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('qrGenerator.bgColor') }}</label>
              <div class="color-picker-wrapper">
                <input type="color" v-model="bgColor" @input="generateQR" />
                <span>{{ bgColor }}</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('qrGenerator.ecLevel') }}</label>
            <div class="ec-options">
              <label v-for="level in ecLevels" :key="level.value" class="radio-label">
                <input type="radio" v-model="ecLevel" :value="level.value" @change="generateQR" />
                {{ level.label }}
              </label>
            </div>
            <p class="ec-hint" v-if="logoDataUrl && (ecLevel === 'L' || ecLevel === 'M')">
              💡 {{ $t('qrGenerator.ecUpgradeHint') }}
            </p>
          </div>

          <div class="form-group">
            <label>{{ $t('qrGenerator.moduleStyle') }}</label>
            <div class="style-options">
              <label
                v-for="style in moduleStyles"
                :key="style.value"
                class="style-option"
                :class="{ active: moduleStyle === style.value }"
                @click="moduleStyle = style.value; generateQR()"
              >
                <span class="style-preview">
                  <svg width="32" height="32" viewBox="0 0 32 32">
                    <template v-if="style.value === 'square'">
                      <rect x="2" y="2" width="12" height="12" :fill="fgColor" />
                      <rect x="18" y="2" width="12" height="12" :fill="fgColor" />
                      <rect x="2" y="18" width="12" height="12" :fill="fgColor" />
                    </template>
                    <template v-else-if="style.value === 'rounded'">
                      <rect x="2" y="2" width="12" height="12" rx="3" :fill="fgColor" />
                      <rect x="18" y="2" width="12" height="12" rx="3" :fill="fgColor" />
                      <rect x="2" y="18" width="12" height="12" rx="3" :fill="fgColor" />
                    </template>
                    <template v-else-if="style.value === 'dots'">
                      <circle cx="8" cy="8" r="6" :fill="fgColor" />
                      <circle cx="24" cy="8" r="6" :fill="fgColor" />
                      <circle cx="8" cy="24" r="6" :fill="fgColor" />
                    </template>
                    <template v-else-if="style.value === 'diamond'">
                      <polygon points="8,2 14,8 8,14 2,8" :fill="fgColor" />
                      <polygon points="24,2 30,8 24,14 18,8" :fill="fgColor" />
                      <polygon points="8,18 14,24 8,30 2,24" :fill="fgColor" />
                    </template>
                  </svg>
                </span>
                <span class="style-label">{{ $t('qrGenerator.styles.' + style.labelKey) }}</span>
              </label>
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('qrGenerator.logoLabel') }}</label>
            <div class="logo-upload-area">
              <div
                class="logo-drop-zone"
                :class="{ 'has-logo': logoDataUrl }"
                @click="$refs.logoInput.click()"
                @dragover.prevent
                @drop.prevent="handleLogoDrop"
              >
                <img v-if="logoDataUrl" :src="logoDataUrl" class="logo-thumbnail" alt="logo" />
                <template v-else>
                  <span class="upload-icon">📷</span>
                  <span class="upload-text">{{ $t('qrGenerator.logoUploadHint') }}</span>
                </template>
              </div>
              <input
                ref="logoInput"
                type="file"
                accept="image/*"
                style="display:none"
                @change="handleLogoSelect"
              />
              <button v-if="logoDataUrl" class="btn btn-sm btn-danger" @click="removeLogo">{{ $t('qrGenerator.logoRemove') }}</button>
              <div class="logo-size-control" v-if="logoDataUrl">
                <label>{{ $t('qrGenerator.logoSize') }}</label>
                <input type="range" v-model.number="logoSizePercent" min="10" max="30" step="1" @input="generateQR" />
                <span class="size-value">{{ logoSizePercent }}%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="qr-preview">
          <div class="preview-box" :style="{ width: size + 'px', height: size + 'px' }">
            <canvas ref="qrCanvas" v-show="content.trim()"></canvas>
            <div class="empty-state" v-if="!content.trim()">
              <span class="empty-icon">📱</span>
              <p>{{ $t('qrGenerator.emptyHint') }}</p>
            </div>
          </div>
          <div class="preview-actions" v-if="content.trim()">
            <button class="btn btn-primary" @click="downloadPNG">{{ $t('qrGenerator.downloadPNG') }}</button>
            <button class="btn btn-secondary" @click="downloadSVG">{{ $t('qrGenerator.downloadSVG') }}</button>
            <button class="btn btn-secondary" @click="copyToClipboard">{{ $t('qrGenerator.copy') }}</button>
          </div>
          <div class="success-msg" v-if="successMsg">{{ successMsg }}</div>
        </div>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('qrGenerator.whatIs') }}</h2>
      <p>{{ $t('qrGenerator.whatIsDesc') }}</p>
      <h3>{{ $t('qrGenerator.useCasesTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('qrGenerator.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h3>{{ $t('qrGenerator.tipsTitle') }}</h3>
      <ul>
        <li v-for="(tip, i) in $t('qrGenerator.tips')" :key="i">{{ tip }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QrGenerator',
  data() {
    return {
      content: '',
      size: 256,
      fgColor: '#000000',
      bgColor: '#ffffff',
      ecLevel: 'M',
      successMsg: '',
      ecLevels: [
        { value: 'L', label: 'L (7%)' },
        { value: 'M', label: 'M (15%)' },
        { value: 'Q', label: 'Q (25%)' },
        { value: 'H', label: 'H (30%)' }
      ],
      // Logo 相关
      logoDataUrl: null,
      logoImage: null,
      logoSizePercent: 20,
      // 模块样式
      moduleStyle: 'square',
      moduleStyles: [
        { value: 'square', labelKey: 'square' },
        { value: 'rounded', labelKey: 'rounded' },
        { value: 'dots', labelKey: 'dots' },
        { value: 'diamond', labelKey: 'diamond' }
      ]
    }
  },
  computed: {
    // 实际使用的纠错级别：有 Logo 时自动提升到至少 H
    effectiveEcLevel() {
      if (this.logoDataUrl) {
        return 'H'
      }
      return this.ecLevel
    }
  },
  methods: {
    generateQR() {
      if (!this.content.trim()) return
      if (!window._qrCodeFactory) return
      this.$nextTick(() => {
        const canvas = this.$refs.qrCanvas
        if (!canvas) return
        canvas.width = this.size
        canvas.height = this.size
        const ctx = canvas.getContext('2d')

        // 使用实际纠错级别（有 Logo 时自动提升）
        const qrData = this.encodeQR(this.content, this.effectiveEcLevel)
        if (!qrData) return

        const moduleCount = qrData.length
        const cellSize = this.size / moduleCount

        // 绘制背景
        ctx.fillStyle = this.bgColor
        ctx.fillRect(0, 0, this.size, this.size)

        // 计算 Logo 覆盖区域（用于跳过被 Logo 遮挡的模块）
        let logoArea = null
        if (this.logoDataUrl && this.logoImage) {
          const logoPixelSize = this.size * this.logoSizePercent / 100
          const logoX = (this.size - logoPixelSize) / 2
          const logoY = (this.size - logoPixelSize) / 2
          // 增加一点 padding，避免 Logo 与码字太近
          const padding = cellSize * 1
          logoArea = {
            x1: logoX - padding,
            y1: logoY - padding,
            x2: logoX + logoPixelSize + padding,
            y2: logoY + logoPixelSize + padding
          }
        }

        // 绘制模块（不同样式）
        ctx.fillStyle = this.fgColor
        for (let row = 0; row < moduleCount; row++) {
          for (let col = 0; col < moduleCount; col++) {
            if (!qrData[row][col]) continue

            const x = col * cellSize
            const y = row * cellSize

            // 跳过 Logo 覆盖区域的模块
            if (logoArea) {
              const cx = x + cellSize / 2
              const cy = y + cellSize / 2
              if (cx >= logoArea.x1 && cx <= logoArea.x2 && cy >= logoArea.y1 && cy <= logoArea.y2) {
                continue
              }
            }

            this.drawModule(ctx, x, y, cellSize, this.moduleStyle)
          }
        }

        // 绘制 Logo
        if (this.logoDataUrl && this.logoImage) {
          const logoPixelSize = this.size * this.logoSizePercent / 100
          const logoX = (this.size - logoPixelSize) / 2
          const logoY = (this.size - logoPixelSize) / 2
          const borderRadius = 6
          const padding = 4

          // 白色背景圆角矩形
          ctx.fillStyle = '#ffffff'
          this.roundRect(ctx, logoX - padding, logoY - padding, logoPixelSize + padding * 2, logoPixelSize + padding * 2, borderRadius + 2)
          ctx.fill()

          // 裁切圆角绘制 Logo
          ctx.save()
          ctx.beginPath()
          this.roundRect(ctx, logoX, logoY, logoPixelSize, logoPixelSize, borderRadius)
          ctx.clip()
          ctx.drawImage(this.logoImage, logoX, logoY, logoPixelSize, logoPixelSize)
          ctx.restore()
        }
      })
    },

    // 绘制单个模块（不同样式）
    drawModule(ctx, x, y, size, style) {
      switch (style) {
        case 'rounded': {
          const r = size * 0.3
          ctx.beginPath()
          this.roundRect(ctx, x, y, size, size, r)
          ctx.fill()
          break
        }
        case 'dots': {
          const radius = size * 0.45
          ctx.beginPath()
          ctx.arc(x + size / 2, y + size / 2, radius, 0, Math.PI * 2)
          ctx.fill()
          break
        }
        case 'diamond': {
          const cx = x + size / 2
          const cy = y + size / 2
          const half = size * 0.5
          ctx.beginPath()
          ctx.moveTo(cx, cy - half)
          ctx.lineTo(cx + half, cy)
          ctx.lineTo(cx, cy + half)
          ctx.lineTo(cx - half, cy)
          ctx.closePath()
          ctx.fill()
          break
        }
        case 'square':
        default:
          ctx.fillRect(x, y, size, size)
          break
      }
    },

    // 辅助方法：绘制圆角矩形路径
    roundRect(ctx, x, y, w, h, r) {
      if (r > w / 2) r = w / 2
      if (r > h / 2) r = h / 2
      ctx.beginPath()
      ctx.moveTo(x + r, y)
      ctx.arcTo(x + w, y, x + w, y + h, r)
      ctx.arcTo(x + w, y + h, x, y + h, r)
      ctx.arcTo(x, y + h, x, y, r)
      ctx.arcTo(x, y, x + w, y, r)
      ctx.closePath()
    },

    // Logo 相关方法
    handleLogoSelect(e) {
      const file = e.target.files && e.target.files[0]
      if (file) this.loadLogoFile(file)
    },

    handleLogoDrop(e) {
      const file = e.dataTransfer.files && e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.loadLogoFile(file)
      }
    },

    loadLogoFile(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.logoDataUrl = e.target.result
        const img = new Image()
        img.onload = () => {
          this.logoImage = img
          this.generateQR()
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    },

    removeLogo() {
      this.logoDataUrl = null
      this.logoImage = null
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = ''
      }
      this.generateQR()
    },

    encodeQR(text, ecLevel) {
      try {
        // qrcode-generator API: qrcode(typeNumber, errorCorrectionLevel)
        // typeNumber=0 表示自动选择版本, ecLevel 为 'L'/'M'/'Q'/'H'
        const qr = window._qrCodeFactory(0, ecLevel)
        qr.addData(text)
        qr.make()
        const count = qr.getModuleCount()
        const data = []
        for (let r = 0; r < count; r++) {
          data[r] = []
          for (let c = 0; c < count; c++) {
            data[r][c] = qr.isDark(r, c)
          }
        }
        return data
      } catch (e) {
        console.error('QR encode error:', e)
        return null
      }
    },

    downloadPNG() {
      const canvas = this.$refs.qrCanvas
      if (!canvas) return
      const link = document.createElement('a')
      link.download = 'qrcode.png'
      link.href = canvas.toDataURL('image/png')
      link.click()
    },

    downloadSVG() {
      const qrData = this.encodeQR(this.content, this.effectiveEcLevel)
      if (!qrData) return
      const count = qrData.length
      const cellSize = 10
      const svgSize = count * cellSize

      // 计算 Logo 区域
      let logoArea = null
      if (this.logoDataUrl && this.logoImage) {
        const logoSvgSize = svgSize * this.logoSizePercent / 100
        const logoX = (svgSize - logoSvgSize) / 2
        const logoY = (svgSize - logoSvgSize) / 2
        const padding = cellSize * 1
        logoArea = {
          x1: logoX - padding, y1: logoY - padding,
          x2: logoX + logoSvgSize + padding, y2: logoY + logoSvgSize + padding
        }
      }

      let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgSize} ${svgSize}" width="${this.size}" height="${this.size}">`
      svg += `<rect width="${svgSize}" height="${svgSize}" fill="${this.bgColor}"/>`

      for (let r = 0; r < count; r++) {
        for (let c = 0; c < count; c++) {
          if (!qrData[r][c]) continue
          const x = c * cellSize
          const y = r * cellSize

          if (logoArea) {
            const cx = x + cellSize / 2
            const cy = y + cellSize / 2
            if (cx >= logoArea.x1 && cx <= logoArea.x2 && cy >= logoArea.y1 && cy <= logoArea.y2) continue
          }

          switch (this.moduleStyle) {
            case 'dots':
              svg += `<circle cx="${x + cellSize / 2}" cy="${y + cellSize / 2}" r="${cellSize * 0.45}" fill="${this.fgColor}"/>`
              break
            case 'rounded':
              svg += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" rx="${cellSize * 0.3}" fill="${this.fgColor}"/>`
              break
            case 'diamond': {
              const cx = x + cellSize / 2
              const cy = y + cellSize / 2
              const half = cellSize * 0.5
              svg += `<polygon points="${cx},${cy - half} ${cx + half},${cy} ${cx},${cy + half} ${cx - half},${cy}" fill="${this.fgColor}"/>`
              break
            }
            default:
              svg += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${this.fgColor}"/>`
          }
        }
      }

      // SVG 中嵌入 Logo
      if (this.logoDataUrl && this.logoImage) {
        const logoSvgSize = svgSize * this.logoSizePercent / 100
        const logoX = (svgSize - logoSvgSize) / 2
        const logoY = (svgSize - logoSvgSize) / 2
        const pad = 4 * (cellSize / 10)
        const br = 6 * (cellSize / 10)
        svg += `<rect x="${logoX - pad}" y="${logoY - pad}" width="${logoSvgSize + pad * 2}" height="${logoSvgSize + pad * 2}" rx="${br + 2}" fill="#ffffff"/>`
        svg += `<clipPath id="logoClip"><rect x="${logoX}" y="${logoY}" width="${logoSvgSize}" height="${logoSvgSize}" rx="${br}"/></clipPath>`
        svg += `<image href="${this.logoDataUrl}" x="${logoX}" y="${logoY}" width="${logoSvgSize}" height="${logoSvgSize}" clip-path="url(#logoClip)"/>`
      }

      svg += '</svg>'
      const blob = new Blob([svg], { type: 'image/svg+xml' })
      const link = document.createElement('a')
      link.download = 'qrcode.svg'
      link.href = URL.createObjectURL(blob)
      link.click()
      URL.revokeObjectURL(link.href)
    },

    async copyToClipboard() {
      const canvas = this.$refs.qrCanvas
      if (!canvas) return
      try {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
        this.successMsg = this.$t('qrGenerator.copySuccess')
        setTimeout(() => { this.successMsg = '' }, 2000)
      } catch {
        // fallback
        this.successMsg = this.$t('qrGenerator.copyFail')
        setTimeout(() => { this.successMsg = '' }, 2000)
      }
    }
  },
  mounted() {
    // 加载 qrcode-generator 库
    if (!window._qrCodeFactory) {
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js'
      script.onload = () => {
        // qrcode-generator 加载后，window.qrcode 是一个工厂函数
        // 用法：qrcode(typeNumber, errorCorrectionLevel) 返回 QR 对象
        window._qrCodeFactory = window.qrcode
        this.content = 'https://tool.moonrise.wiki'
        this.generateQR()
      }
      document.head.appendChild(script)
    } else {
      this.content = 'https://tool.moonrise.wiki'
      this.generateQR()
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1000px; margin: 0 auto; }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 28px; color: var(--text-primary); margin-bottom: 8px; }
.tool-header p { color: var(--text-secondary); font-size: 16px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }

.qr-layout { display: flex; gap: 32px; }
.qr-form { flex: 1; }
.qr-preview { display: flex; flex-direction: column; align-items: center; gap: 16px; }

.form-group { margin-bottom: 20px; }
.form-group label { display: block; font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.input-area { width: 100%; padding: 12px 16px; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); color: var(--text-primary); font-size: 14px; resize: vertical; box-sizing: border-box; outline: none; font-family: inherit; }
.input-area:focus { border-color: var(--accent-color); }

.form-row { display: flex; gap: 16px; }
.color-row .form-group { flex: 1; }

.size-control { display: flex; align-items: center; gap: 12px; }
.size-control input[type="range"] { flex: 1; accent-color: var(--accent-color); }
.size-value { font-size: 13px; color: var(--text-secondary); min-width: 80px; text-align: right; }

.color-picker-wrapper { display: flex; align-items: center; gap: 8px; }
.color-picker-wrapper input[type="color"] { width: 40px; height: 36px; border: 1px solid var(--border-color); border-radius: 8px; cursor: pointer; background: none; padding: 2px; }
.color-picker-wrapper span { font-size: 13px; color: var(--text-secondary); font-family: monospace; }

.ec-options { display: flex; gap: 16px; flex-wrap: wrap; }
.radio-label { display: flex; align-items: center; gap: 4px; font-size: 14px; color: var(--text-secondary); cursor: pointer; }
.radio-label input { accent-color: var(--accent-color); }
.ec-hint { font-size: 12px; color: var(--accent-color); margin-top: 6px; margin-bottom: 0; }

/* 模块样式选择 */
.style-options { display: flex; gap: 10px; flex-wrap: wrap; }
.style-option { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px 12px; border: 2px solid var(--border-color); border-radius: 10px; cursor: pointer; transition: all 0.2s; background: var(--bg-input); }
.style-option:hover { border-color: var(--accent-color); }
.style-option.active { border-color: var(--accent-color); background: var(--accent-color-light, rgba(59, 130, 246, 0.08)); }
.style-preview { display: flex; align-items: center; justify-content: center; }
.style-label { font-size: 12px; color: var(--text-secondary); }
.style-option.active .style-label { color: var(--accent-color); font-weight: 600; }

/* Logo 上传区域 */
.logo-upload-area { display: flex; flex-direction: column; gap: 10px; }
.logo-drop-zone { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; width: 100%; min-height: 80px; border: 2px dashed var(--border-color); border-radius: 10px; cursor: pointer; transition: all 0.2s; background: var(--bg-input); padding: 12px; box-sizing: border-box; }
.logo-drop-zone:hover { border-color: var(--accent-color); background: var(--accent-color-light, rgba(59, 130, 246, 0.05)); }
.logo-drop-zone.has-logo { border-style: solid; padding: 8px; min-height: auto; }
.upload-icon { font-size: 28px; }
.upload-text { font-size: 13px; color: var(--text-tertiary); }
.logo-thumbnail { max-width: 80px; max-height: 80px; border-radius: 6px; object-fit: contain; }
.btn-sm { padding: 4px 12px; font-size: 12px; }
.btn-danger { background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer; transition: opacity 0.2s; }
.btn-danger:hover { opacity: 0.85; }
.logo-size-control { display: flex; align-items: center; gap: 8px; }
.logo-size-control label { font-size: 13px; color: var(--text-secondary); min-width: auto; margin-bottom: 0; }
.logo-size-control input[type="range"] { flex: 1; accent-color: var(--accent-color); }

.preview-box { display: flex; align-items: center; justify-content: center; background: white; border-radius: 12px; border: 1px solid var(--border-color); overflow: hidden; }
.preview-box canvas { display: block; width: 100%; height: 100%; }
.empty-state { text-align: center; color: var(--text-tertiary); }
.empty-icon { font-size: 48px; display: block; margin-bottom: 8px; }
.empty-state p { font-size: 14px; }

.preview-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
.btn { padding: 8px 16px; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; transition: all 0.2s; }
.btn-primary { background: var(--accent-color); color: white; }
.btn-primary:hover { opacity: 0.9; }
.btn-secondary { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); }
.btn-secondary:hover { border-color: var(--accent-color); }

.success-msg { font-size: 14px; color: var(--success-color, #22c55e); text-align: center; }

.info-card h2 { font-size: 20px; color: var(--text-primary); margin-bottom: 12px; }
.info-card h3 { font-size: 16px; color: var(--text-primary); margin: 16px 0 8px; }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; }
.info-card ul { padding-left: 20px; margin: 0; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 2; }

@media (max-width: 768px) {
  .qr-layout { flex-direction: column; }
  .form-row { flex-direction: column; }
  .preview-box { max-width: 100%; }
}
</style>
